import {
  ControlledDropdownWithInput,
  ControlledInputField,
} from '../../../../components/form-fields';
import { HeadingSubform } from './HeadingSubform';
import { TextWithTooltip } from '@payright/web-components';
import getConstants from '../../../../util/constants';
import type { DeepMap, FieldError } from 'react-hook-form';
import type { FormHouseholdExpensesFormData } from './FormHouseholdExpenses';
import { SubFormContent, SubFormFields } from './SubFormStyles';

const { INCOME_FREQUENCIES } = getConstants();

export const LIVING_EXPENSES_TOOLTIP =
  'To assess your application, we use the Household Expenditure Measure (HEM) which is a standard benchmark used to estimate total household living expenses. So we can estimate your share of these costs, please provide an estimate of your day-to-day expenses such as groceries, household bills, transport and personal spending. Do not include what you pay in rent (if applicable), or any financial expenses such as loan repayments or credit card repayments.';

export const LOAN_COUNT_DESCRIPTION =
  'How many Buy-now-pay-later (BNPL) loans, small loans (less than $2000), or Consumer Leases do you have?';

export const LOAN_COUNT_TOOLTIP =
  'Please provide the total number of outstanding Buy-now-pay-later (BNPL) loans, small loans (less than $2000), such as payday loans or small personal loans, and the number of consumer leases for items such as laptops, TVs or fridges etc.';

export const FINANCIAL_EXPENSES_TOOLTIP =
  'Please provide an estimate of any existing financial expenses such as personal loans and credit card repayments.';

type SubformLivingExpensesProps = {
  showFinancialExpenses: boolean;
  errors: DeepMap<FormHouseholdExpensesFormData['expenseDetails'], FieldError>;
};

export const SubformLivingExpenses = ({
  errors,
  showFinancialExpenses,
}: SubformLivingExpensesProps) => {
  return (
    <SubFormContent>
      <HeadingSubform>Living and Financial Expenses</HeadingSubform>
      <SubFormFields>
        <ControlledDropdownWithInput
          className="grid-all-column"
          dropdownName="expenseDetails.livingExpensesFrequency"
          inputName="expenseDetails.livingExpensesAmount"
          inputPrefix="$"
          options={INCOME_FREQUENCIES}
          inputRules={{
            required: 'Living expenses is required',
            min: {
              value: 0,
              message: 'Living expenses cannot be negative',
            },
            validate: (value: any) => {
              if (isNaN(value)) {
                return 'Living expenses must be a number';
              }
            },
          }}
          error={errors.livingExpensesAmount?.message}
        >
          <div className="label-with-tooltip">
            <span>
              <span className="underline">Your share of</span> living expenses (excluding rent and
              debt repayments)
            </span>
            <TextWithTooltip content={LIVING_EXPENSES_TOOLTIP}>What's this?</TextWithTooltip>
            <span className="label-secondary">
              If you share any expenses, only include{' '}
              <span className="underline">your own portion</span>
            </span>
          </div>
        </ControlledDropdownWithInput>
        <ControlledInputField
          className="grid-all-column"
          name="expenseDetails.loanCount"
          rules={{
            required: 'Please enter a number between 0 & 100',
            min: {
              value: 0,
              message: 'Please enter a number between 0 & 100',
            },
            max: {
              value: 100,
              message: 'Please enter a number between 0 & 100',
            },
          }}
          decimals={0}
          type="number"
          error={errors.loanCount?.message}
        >
          <div className="label-with-tooltip">
            {LOAN_COUNT_DESCRIPTION}
            <TextWithTooltip content={LOAN_COUNT_TOOLTIP}>What's this?</TextWithTooltip>
          </div>
        </ControlledInputField>
        {showFinancialExpenses && (
          <ControlledDropdownWithInput
            dropdownName="expenseDetails.financialExpensesFrequency"
            inputName="expenseDetails.financialExpensesAmount"
            className="grid-all-column"
            inputPrefix="$"
            options={INCOME_FREQUENCIES}
            inputRules={{
              required: 'Financial expenses is required',
              min: {
                value: 0,
                message: 'Financial expenses cannot be negative',
              },
              validate: (value: any) => {
                if (isNaN(value)) {
                  return 'Financial expenses  must be a number';
                }
              },
            }}
            error={errors.financialExpensesAmount?.message}
          >
            <div className="label-with-tooltip">
              <span>
                <span className="underline">Your share</span> of financial expenses (excluding any
                mortgage repayments)
              </span>
              <TextWithTooltip content={FINANCIAL_EXPENSES_TOOLTIP}>What's this?</TextWithTooltip>
              <span className="label-secondary">
                If you share any expenses, only include{' '}
                <span className="underline">your own portion</span>
              </span>
            </div>
          </ControlledDropdownWithInput>
        )}
      </SubFormFields>
    </SubFormContent>
  );
};
